/**
 * nav-model.ts — Modelo de navegação do shell do Órbita.
 *
 * Define grupos e itens de navegação com role mínima e moduleId.
 * Filtrado pelo useVisibleNav hook com base nos claims do usuário.
 *
 * Referência: NAVEGACAO.md §3.3
 */

import type { OrbModuleId, OrbRole } from "@/features/auth/auth.types";

export interface NavItem {
  id: OrbModuleId;
  label: string;
  path: string;
  /** Role mínima para visualizar o item */
  minRole: OrbRole;
}

/** Id estável de segmento (grupo do menu) — chave de persistência da config de menu.
 *  Estável independente do label (renomear o label não quebra a config). */
export type NavGroupId =
  | "visao-geral"
  | "gestao-empresarial"
  | "gestao-comercial"
  | "assistencial"
  | "cadastros"
  | "configuracao";

export interface NavGroup {
  /** Id estável do segmento (chave de persistência); o label é só apresentação. */
  id: NavGroupId;
  label: string;
  items: NavItem[];
}

/** Estrutura completa de navegação do Órbita (NAVEGACAO.md §3.3) */
export const NAV_GROUPS: NavGroup[] = [
  {
    id: "visao-geral",
    label: "Visão Geral",
    items: [
      {
        id: "visao-executiva",
        label: "Visão Executiva",
        path: "/app",
        minRole: "viewer",
      },
    ],
  },
  {
    id: "gestao-empresarial",
    label: "Gestão Empresarial",
    items: [
      // Módulo único "Fluxo de Caixa": a família de caixa (Fluxo Projetado,
      // Necessidade, Cenários, Contas a Receber/Pagar, Recebimentos, Importação
      // de Pagamentos) vive como ABAS dentro desta página (FluxoCaixaLayout),
      // não como itens soltos no menu. Decisão do usuário 2026-09-02.
      { id: "fluxo-caixa",           label: "Fluxo de Caixa",         path: "/app/financeiro/fluxo-caixa",           minRole: "financeiro" },
      { id: "compras",                label: "Compras",                path: "/app/financeiro/compras",                minRole: "financeiro" },
      { id: "estoque",                label: "Estoque",                path: "/app/financeiro/estoque",                minRole: "financeiro" },
      { id: "custos",                 label: "Custos",                 path: "/app/financeiro/custos",                 minRole: "financeiro" },
      { id: "rateios",                label: "Rateios",                path: "/app/financeiro/rateios",                minRole: "financeiro" },
      { id: "precificacao",           label: "Precificação",           path: "/app/financeiro/precificacao",           minRole: "financeiro" },
    ],
  },
  {
    id: "gestao-comercial",
    label: "Gestão Comercial",
    items: [
      { id: "vendas",       label: "Vendas",        path: "/app/comercial/vendas",       minRole: "financeiro" },
      { id: "procedimentos", label: "Procedimentos", path: "/app/comercial/procedimentos", minRole: "financeiro" },
    ],
  },
  {
    id: "assistencial",
    label: "Assistencial",
    items: [
      { id: "assistencial", label: "Explorador Assistencial", path: "/app/assistencial", minRole: "viewer" },
    ],
  },
  {
    id: "cadastros",
    label: "Cadastros",
    items: [
      { id: "fornecedores",     label: "Fornecedores",     path: "/app/cadastros/fornecedores",     minRole: "financeiro" },
      { id: "fontes-receita",   label: "Fontes de Receita", path: "/app/cadastros/fontes-receita",  minRole: "financeiro" },
      { id: "estrutura-fisica", label: "Estrutura Física", path: "/app/cadastros/estrutura-fisica", minRole: "financeiro" },
      { id: "fontes-dados",     label: "Fontes de Dados",  path: "/app/cadastros/fontes-dados",     minRole: "financeiro" },
    ],
  },
  {
    id: "configuracao",
    label: "Configuração",
    items: [
      { id: "estrutura",     label: "Estrutura",     path: "/app/estrutura",     minRole: "viewer" },
      { id: "integracoes",   label: "Integrações",   path: "/app/integracoes",   minRole: "admin" },
      { id: "usuarios",      label: "Usuários",      path: "/app/usuarios",      minRole: "admin" },
      { id: "configuracoes", label: "Configurações", path: "/app/configuracoes", minRole: "admin" },
    ],
  },
];

/** Itens de BottomNav para mobile (NAVEGACAO.md §3.4) */
export interface BottomNavItem {
  label: string;
  path: string;
  ariaLabel: string;
}

export const BOTTOM_NAV_ITEMS: BottomNavItem[] = [
  { label: "Visão",      path: "/app",                        ariaLabel: "Visão Executiva" },
  { label: "Financeiro", path: "/app/financeiro/fluxo-caixa", ariaLabel: "Fluxo de Caixa" },
  { label: "Comercial",  path: "/app/comercial/vendas",        ariaLabel: "Gestão Comercial" },
  { label: "Assistencial", path: "/app/assistencial",          ariaLabel: "Explorador Assistencial" },
  { label: "Config",     path: "/app/configuracoes",           ariaLabel: "Configurações" },
];
